import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import type { CalibrationStatusData } from "@/hooks/queries/calibration-status-types";
import { useCalibrationStatus } from "@/hooks/queries/useCalibrationStatus";
import {
  DEADLINE_ANCHOR_FOOT,
  DEADLINE_REVIEW_CTA_LABEL,
  DEADLINE_REVIEW_MAILTO,
  DEADLINE_STATE_BADGE_VARIANT,
  DEADLINE_STATE_LABEL,
  HOLD_BANNER_HEADING,
  HOLD_GUARANTEES,
  HOLD_INERT_REASON,
  deadlineAnchorDay,
  deadlineDaysLabel,
  deadlineWindowDays,
  type ShownDeadlineState,
} from "@/lib/calibration-deadline";
import { formatDate } from "@/lib/format-date";
import { CalibrationSubNav } from "@/routes/calibration/CalibrationSubNav";

// Story 8.7 (gh-246) — Calibration overview (UX Screen 15). Replaces the Story 8.1
// CalibrationOverviewPlaceholder at /calibration/overview. Renders inside the
// AppShell for a clinician routed here by the Story 8.1 CalibrationGate.
//
// Story 20.1 (FR86) makes it the page that answers "how long do I have?" as well
// as "what is this?": Screen 38 panels 1-3 put the deadline at the top, above the
// narrative, which is why this screen now reads GET /calibration/status (it was
// deliberately data-free before). Same hook and same query key as the case board
// and the gate, so an already-hydrated cache costs this page nothing.
//
// The page h1 is "Calibration" (matching the nav label + the placeholder it replaces);
// the mockup's "How calibration works" is the content h2. Tokens only, bg-primary
// canvas (AppShell provides it); WCAG 2.1 AA.
const HOW_IT_WORKS = [
  "You will complete 5 standardized case studies with video material hosted directly in this platform.",
  "Your scores are compared against gold-standard expert references.",
  "The platform stores your calibration outcome on your account and uses it automatically in live scoring.",
];

const TOTAL_CASES = 5;

/**
 * One cell of Screen 38's four-stat grid: a label, a figure, and an optional
 * foot that says what the figure means.
 *
 * `tabular-nums` sits on the FIGURE unconditionally (CLAUDE.md), because all
 * four figures here are numeric — two dates, a countdown and a fraction — and
 * proportional digits leave them jittering against each other in a row the
 * clinician reads as a row.
 *
 * `emphasis` has THREE values because Screen 38 draws three tile treatments, not
 * two. A boolean would collapse `"muted"` into `"none"` and render a stopped
 * clock exactly like a running one — the countdown in `held` is not merely
 * unemphasised, it is inert, and the mockup says so in its own words: "the
 * countdown stops rather than alarms: inert surface, muted figure". Token
 * mapping is the hi-fi sheet's own: `--line-strong` → `--border-strong`,
 * `--bg-alt` → `--bg-tertiary` (identical values), and `--muted #68757d` →
 * `--text-tertiary`, which is darker and therefore the safer side of WCAG AA.
 */
type StatEmphasis = "none" | "warn" | "muted";

const STAT_SURFACE: Record<StatEmphasis, string> = {
  none: "border-[color:var(--border-primary)] bg-[color:var(--bg-primary)]",
  warn: "border-[color:var(--bg-warning-secondary)] bg-[color:var(--bg-warning-primary)]",
  muted: "border-[color:var(--border-strong)] bg-[color:var(--bg-tertiary)]",
};

const STAT_FIGURE: Record<StatEmphasis, string> = {
  none: "text-[color:var(--text-primary)]",
  warn: "text-[color:var(--text-warning)]",
  muted: "text-[color:var(--text-tertiary)]",
};

function DeadlineStat({
  label,
  value,
  foot,
  emphasis = "none",
}: {
  label: string;
  value: string;
  foot?: string | null;
  emphasis?: StatEmphasis;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={`rounded-2xl border px-5 py-4 ${STAT_SURFACE[emphasis]}`}
    >
      <div className="text-sm text-[color:var(--text-tertiary)]">{label}</div>
      <div
        className={`tabular-nums text-2xl font-semibold ${STAT_FIGURE[emphasis]}`}
      >
        {value}
      </div>
      {foot ? (
        <div className="mt-1 text-xs text-[color:var(--text-tertiary)]">{foot}</div>
      ) : null}
    </div>
  );
}

/**
 * Screen 38 panels 1-3 — the FR86 deadline, for a clinician who has one.
 *
 * Rendered for `active`, `due_soon` and `held`; `not_applicable` never reaches
 * here, which is the exemption and is the whole of AC6 on this surface.
 *
 * `held` gets the state's own wording and NOTHING that claims a capability has
 * been withdrawn. Story 20.1 changes no behaviour (AC16) — the submit path is
 * still open — so panel 3's inert Case Studies tab, disabled CTA and four-point
 * hold banner arrive with the gate in Story 20.2. Rendering the elapsed dates
 * and the shared "On hold" label is the part that is true today.
 *
 * Every optional field is guarded with `!= null` rather than inferred from the
 * state. `deadline_state` is never withheld from anybody, but the dates and the
 * countdown ride the `view_calibration_numeric_values` scope (AC14), so an
 * `active` payload carrying no `calibration_deadline_at` is a legitimate wire —
 * and the em dash `formatDate` returns for an absent date is the honest render.
 */
function DeadlineSection({
  data,
  state,
}: {
  data: CalibrationStatusData;
  state: ShownDeadlineState;
}) {
  const deadline = formatDate(data.calibration_deadline_at);
  const windowDays = deadlineWindowDays(
    data.deadline_anchor_at,
    data.calibration_deadline_at,
  );
  const daysRemaining = data.days_remaining;
  const completed = Math.max(0, Math.min(TOTAL_CASES, data.completed_cases));

  return (
    <section
      aria-label="Calibration deadline"
      className="flex flex-col gap-4 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] p-6"
    >
      {/* Panel 2's notice. `role="status"` (polite) rather than the Banner's own
          default of `alert` for a warning: fourteen days out is a reminder, and
          the design system's posture is desaturated and calm, never clinical
          alarm. Nothing below it is disabled and nothing is taken away. */}
      {state === "due_soon" && daysRemaining != null ? (
        <Banner variant="warn" role="status">
          <p>
            <strong className="tabular-nums">
              {daysRemaining === 0
                ? "Today is the last day to complete calibration."
                : `${deadlineDaysLabel(daysRemaining)} left to complete calibration.`}
            </strong>{" "}
            Your case studies stay open and nothing changes before{" "}
            <span className="tabular-nums">{deadline}</span>. If you need more time,
            ask CITS for a deadline review before that date.
          </p>
        </Banner>
      ) : null}

      {/* Panel 3's hold banner (AC9). `warn`, never `danger`, and `role="status"`
          rather than `alert`: the countdown has STOPPED, not failed, and the
          mockup's caption says so in its own words. Nothing on this surface
          reaches for a `--*-error-*` token, WITHOUT EXCEPTION — the held badge
          below moved to the muted-warning pair in DW-669, so the mockup's
          `.pill.blocked` is no longer transcribed here.

          The four guarantees are transcribed from `HOLD_GUARANTEES`, which is
          where this copy lives so the Profile card cannot paraphrase it. */}
      {state === "held" ? (
        <Banner variant="warn" role="status">
          <strong>{HOLD_BANNER_HEADING}</strong>
          <ul className="flex list-disc flex-col gap-1 pl-5">
            {HOLD_GUARANTEES.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
            Calibration deadline
          </h2>
          <p className="text-sm text-[color:var(--text-tertiary)]">
            {state === "held" ? (
              <>
                The window closed on <span className="tabular-nums">{deadline}</span>.
              </>
            ) : (
              "Complete all five case studies by this date."
            )}
          </p>
        </div>
        <Badge variant={DEADLINE_STATE_BADGE_VARIANT[state]}>
          {DEADLINE_STATE_LABEL[state]}
        </Badge>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <DeadlineStat
          label="Clock started"
          value={formatDate(deadlineAnchorDay(data.deadline_anchor_at))}
          // DW-660 — the foot names the FIELD, never the event that produced it.
          // The cohort-keyed caption this replaced claimed a certificate approval
          // over a date that may be an activation stamp (a `webapp_legacy`
          // clinician with no upload row) or the platform anchor floor, and
          // neither is visible from this side of the wire.
          foot={DEADLINE_ANCHOR_FOOT}
        />
        <DeadlineStat
          label="Calibration deadline"
          value={deadline}
          foot={
            state === "held"
              ? "Elapsed — hold applied the next day"
              : windowDays != null
                ? `${deadlineDaysLabel(windowDays)} from the start date`
                : null
          }
        />
        <DeadlineStat
          label="Days remaining"
          value={daysRemaining != null ? String(daysRemaining) : "—"}
          foot={
            state === "held" && data.held_since != null
              ? `On hold since ${formatDate(data.held_since)}`
              : null
          }
          emphasis={
            state === "due_soon" ? "warn" : state === "held" ? "muted" : "none"
          }
        />
        <DeadlineStat
          label="Cases completed"
          value={`${completed} / ${TOTAL_CASES}`}
          foot={state === "held" ? "Saved and retained" : null}
        />
      </div>

      <p className="text-sm text-[color:var(--text-secondary)]">
        {state === "due_soon"
          ? "If the deadline passes, calibration progression is placed on temporary hold. Your account, your clients and your data are kept, and your purchase is not forfeited."
          : state === "held"
            ? "Your account, your clients and your saved case scores are kept, and your purchase is not forfeited. Write to CITS from the email address you sign in with if you need your deadline reviewed."
            : "The window opened when your account setup and credential verification were both complete. If something outside your control gets in the way, CITS can review your deadline."}
      </p>
    </section>
  );
}

/**
 * Panel 3's "What to do next" — the reason a held clinician is never left at a
 * dead end (AC10).
 *
 * The primary CTA is a `mailto:` because a super admin's review is the only
 * thing that lifts the hold and the platform has no in-app route to ask for one
 * (Story 20.3 gives the admin the grant surface, not the clinician a request
 * form). The address is the AMPS Director role inbox, settled by the PM
 * 2026-09-15 — see `DEADLINE_REVIEW_MAILTO`, which also carries the fixed
 * subject line that lets that shared inbox tell these requests apart from the
 * organization traffic it already receives.
 */
/** DW-680 — ties the visible hold reason to the inert "Continue to cases" CTA. */
const HOLD_CTA_REASON_ID = "calibration-overview-hold-cta-reason";

const WHAT_TO_DO_NEXT = [
  "Write to CITS from the email address you sign in with and describe what got in the way.",
  "A super admin can set a new deadline or reactivate your account. Both actions restore your case studies immediately.",
  "You can keep using Profile and Resources while you wait.",
];

function HoldNextSteps() {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
          What to do next
        </h2>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Extensions are granted case by case by a CITS super admin.
        </p>
      </div>
      <ul className="flex list-disc flex-col gap-2 pl-5 text-sm text-[color:var(--text-secondary)]">
        {WHAT_TO_DO_NEXT.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="primary">
          <a href={DEADLINE_REVIEW_MAILTO}>{DEADLINE_REVIEW_CTA_LABEL}</a>
        </Button>
        <Button asChild variant="secondary">
          <Link to="/resources">Open Resources</Link>
        </Button>
      </div>
    </section>
  );
}

export function CalibrationOverviewScreen() {
  const { data } = useCalibrationStatus({ enabled: true });

  // Quiet while the read is pending or has failed — the case board's own posture
  // (a destination renders a Banner there because an empty BOARD reads as broken;
  // an absent deadline section reads as nothing at all, which is correct here).
  // A deadline nobody has read yet is not a deadline of "unknown".
  const state = data?.deadline_state;
  const showDeadline = data != null && state != null && state !== "not_applicable";

  // The single discriminator, read once and passed down (AC12). No date
  // comparison, no day arithmetic, no hold-type inference anywhere below —
  // `deadline_state === "held"` is the whole of it, and `HOLD_INERT_REASON` is
  // the one sentence both inert affordances render.
  const held = state === "held";

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="display-xs text-[color:var(--text-primary)]">Calibration</h1>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Complete the 5 calibration case studies to unlock live AMPS scoring.
        </p>
      </header>

      <CalibrationSubNav heldReason={held ? HOLD_INERT_REASON : undefined} />

      {showDeadline ? (
        <DeadlineSection data={data} state={state} />
      ) : null}

      {held ? <HoldNextSteps /> : null}

      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
          How calibration works
        </h2>
        <ul className="flex list-disc flex-col gap-2 pl-5 text-sm text-[color:var(--text-secondary)]">
          {HOW_IT_WORKS.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>

      {/* "Continue to cases" goes inert while held (AC10, P0-CAL-09): a real
          `<button type="button" disabled>` rather than a styled `<Link>`, so the
          route is not reachable by click, keyboard, middle-click or copy — a
          disabled anchor is still an anchor. The reason is VISIBLE TEXT beside
          it, never the mockup's native `title` (D9), and LINKED to the button
          with `aria-describedby` so it reaches the accessible description and
          not only the page (DW-680).

          It also drops to `secondary` while held. The view's single primary CTA
          is then "Request a deadline review", which is the only thing a held
          clinician CAN do — the mockup makes the same split (`btn primary` on
          the review CTA, a plain `btn` on the disabled one), and the house rule
          allows one primary accent per view. */}
      <div className="flex flex-wrap items-center gap-3">
        {held ? (
          <>
            <Button
              type="button"
              variant="secondary"
              disabled
              aria-disabled="true"
              aria-describedby={HOLD_CTA_REASON_ID}
            >
              Continue to cases
            </Button>
            <span
              id={HOLD_CTA_REASON_ID}
              className="text-sm text-[color:var(--text-tertiary)]"
            >
              {HOLD_INERT_REASON}
            </span>
          </>
        ) : (
          <Button asChild variant="primary">
            <Link to="/calibration/cases">Continue to cases</Link>
          </Button>
        )}
      </div>
    </section>
  );
}

export default CalibrationOverviewScreen;
